import { Link } from "react-router-dom";
import { ArrowRight, Wand2 } from "lucide-react";
import { useTranslation } from "react-i18next";

/** Inline AI Assistant illustration — clean, premium, alive.
 *  Friendly white robot with glowing blue eyes, blue/purple antenna light,
 *  body float, eye blink, subtle head tilt, breathing halo, sparkles.
 *  No finance objects per the spec. */
function AIAdvisorMark() {
  return (
    <svg viewBox="0 0 64 64" width="64" height="64" aria-hidden="true">
      <defs>
        <linearGradient id="advBodyG" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"  stopColor="#FFFFFF" />
          <stop offset="100%" stopColor="#E0E7FF" />
        </linearGradient>
        <linearGradient id="advAntennaG" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"  stopColor="#A5B4FC" />
          <stop offset="100%" stopColor="#6366F1" />
        </linearGradient>
        <radialGradient id="advHalo" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0%"  stopColor="rgba(255,255,255,0.55)" />
          <stop offset="60%" stopColor="rgba(255,255,255,0.08)" />
          <stop offset="100%" stopColor="rgba(255,255,255,0)" />
        </radialGradient>
        <radialGradient id="advEye" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0%"  stopColor="#93C5FD" />
          <stop offset="60%" stopColor="#3B82F6" />
          <stop offset="100%" stopColor="#1E3A8A" />
        </radialGradient>
      </defs>

      {/* Breathing halo */}
      <circle cx="32" cy="34" r="22" fill="url(#advHalo)" className="ai-bg-glow" />

      {/* Floating sparkles around the robot */}
      <g className="qa2-sparkle" style={{ animationDelay: "0.1s" }}>
        <path d="M9 18 L11 20 L9 22 L7 20 Z" fill="#E0E7FF" />
      </g>
      <g className="qa2-sparkle" style={{ animationDelay: "0.7s" }}>
        <path d="M55 22 L57 24 L55 26 L53 24 Z" fill="#C7D2FE" />
      </g>
      <g className="qa2-sparkle" style={{ animationDelay: "1.3s" }}>
        <path d="M13 50 L14.5 51.5 L13 53 L11.5 51.5 Z" fill="#C7D2FE" />
      </g>
      <g className="qa2-sparkle" style={{ animationDelay: "1.8s" }}>
        <path d="M52 48 L53.5 49.5 L52 51 L50.5 49.5 Z" fill="#E0E7FF" />
      </g>

      {/* Body group floats */}
      <g className="ai-body">
        {/* Antenna */}
        <line x1="32" y1="6" x2="32" y2="11" stroke="#6366F1" strokeWidth="1.4" strokeLinecap="round" />
        <circle cx="32" cy="5" r="2.2" fill="url(#advAntennaG)">
          <animate attributeName="r" values="1.8;2.6;1.8" dur="2s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.7;1;0.7" dur="2s" repeatCount="indefinite" />
        </circle>
        {/* Antenna glow */}
        <circle cx="32" cy="5" r="4" fill="none" stroke="#6366F1" strokeOpacity="0.35" strokeWidth="1">
          <animate attributeName="r" values="3;5.5;3" dur="2s" repeatCount="indefinite" />
          <animate attributeName="stroke-opacity" values="0.5;0;0.5" dur="2s" repeatCount="indefinite" />
        </circle>

        {/* Head — rounded square, slight tilt */}
        <g className="ai-head">
          {/* shadow under head */}
          <ellipse cx="32" cy="48" rx="13" ry="2" fill="#000" opacity="0.18" />

          {/* head body */}
          <rect x="14" y="12" width="36" height="34" rx="11" fill="url(#advBodyG)" stroke="#6366F1" strokeWidth="1" />
          {/* Glossy top highlight */}
          <rect x="17" y="14.5" width="30" height="6" rx="6" fill="rgba(255,255,255,0.85)" />
          {/* Gradient bottom accent */}
          <path d="M14 36 Q32 50 50 36 L50 46 L14 46 Z" fill="rgba(99,102,241,0.10)" />

          {/* Cheek blush */}
          <circle cx="19" cy="32" r="2" fill="#FBCFE8" opacity="0.7" />
          <circle cx="45" cy="32" r="2" fill="#FBCFE8" opacity="0.7" />

          {/* Eyes — large, glowing blue, blink */}
          <g className="ai-eye">
            <circle cx="25" cy="28" r="3.6" fill="url(#advEye)" />
            <circle cx="26" cy="27" r="1" fill="#FFFFFF" opacity="0.9" />
          </g>
          <g className="ai-eye">
            <circle cx="39" cy="28" r="3.6" fill="url(#advEye)" />
            <circle cx="40" cy="27" r="1" fill="#FFFFFF" opacity="0.9" />
          </g>

          {/* Smile */}
          <path d="M26 37 Q32 41 38 37" fill="none" stroke="#6366F1" strokeWidth="1.6" strokeLinecap="round" />
        </g>
      </g>
    </svg>
  );
}

export default function AIInsightCard() {
  const { t } = useTranslation();
  return (
    <div className="pcard-ai" data-testid="ai-insight-card">
      <div className="pcard-ai-inner p-5 md:p-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          {/* Left content */}
          <div className="flex min-w-0 items-center gap-4">
            <div className="relative shrink-0">
              <div
                className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-2xl text-white"
                style={{
                  background: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 50%, #2563EB 100%)",
                  boxShadow: "0 14px 28px -10px rgba(99,102,241,0.7), inset 0 1px 0 rgba(255,255,255,0.45)",
                }}
                aria-label={t("ai_advisor")}
              >
                <AIAdvisorMark />
              </div>
              <span
                aria-hidden
                className="absolute -inset-1 rounded-3xl"
                style={{ background: "radial-gradient(closest-side, rgba(99,102,241,0.35), transparent 70%)", filter: "blur(10px)", zIndex: -1 }}
              />
            </div>

            <div className="min-w-0">
              {/* Header: blinking blue dot + AI ADVISOR + YouTube-style LIVE badge */}
              <div className="flex flex-wrap items-center gap-2">
                <span className="p-dot-live" aria-hidden data-testid="ai-status-dot" />
                <span className="text-[11px] font-bold uppercase tracking-[0.20em] text-muted-foreground">
                  {t("ai_advisor")}
                </span>
                <span
                  className="ai-live-badge inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-[0.12em] text-white"
                  aria-label={t("ai_live")}
                  data-testid="ai-live-badge"
                >
                  <span className="ai-live-dot" aria-hidden />
                  {t("ai_live")}
                </span>
              </div>

              {/* Title */}
              <h3 className="mt-0.5 font-display text-[19px] font-extrabold leading-tight tracking-tight md:text-[22px] md:whitespace-nowrap">
                {t("ai_title")}
              </h3>

              {/* Description */}
              <p className="mt-2 max-w-[520px] text-[12.5px] leading-snug text-muted-foreground md:text-[13px]">
                {t("ai_desc")}
              </p>
            </div>
          </div>

          {/* CTA */}
          <Link
            to="/insights?tab=chat"
            data-testid="ai-insight-cta"
            className="group inline-flex shrink-0 items-center gap-2 rounded-full px-5 py-3 text-[14px] font-bold text-white transition-all hover:brightness-110"
            style={{
              background: "linear-gradient(135deg, #6366F1 0%, #2563EB 100%)",
              boxShadow: "0 14px 28px -10px rgba(99,102,241,0.65), inset 0 1px 0 rgba(255,255,255,0.3)",
            }}
          >
            <Wand2 className="h-4 w-4" />
            {t("ai_open_advisor")}
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </Link>
        </div>
      </div>
    </div>
  );
}
